import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Calendar, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { PasswordInput, isStrongPassword } from '../components/PasswordInput';

export const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(formData.password)) {
      toast.error(language === 'fr' ? 'Le mot de passe doit contenir au moins 8 caractères, dont une lettre et un chiffre.' : 'Password must be at least 8 characters with a letter and a number.');
      return;
    }
    setLoading(true);

    try {
      await register(formData);
      toast.success(language === 'fr' ? 'Compte créé avec succès !' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || t('errorOccurred');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 hero-bg">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-3xl p-8 sm:p-10 shadow-xl animate-scale-in">
          {/* Header */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_stop-tabac-2/artifacts/u4es5x0d_Application.jpeg"
              alt="ZéphyrBreath"
              className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4"
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Créez votre compte' : 'Create your account'}
            </h1>
            <p className="text-gray-600">
              {language === 'fr' 
                ? 'Commencez votre parcours vers une vie sans tabac'
                : 'Start your journey to a smoke-free life'}
            </p>
          </div>

          {/* Google Sign-up */}
          <GoogleSignInButton testId="google-signin-register-btn" />

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase tracking-wider">
              {language === 'fr' ? 'ou' : 'or'}
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-gray-700 font-medium">
                  {t('firstName')}
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Jean"
                    className="pl-12 h-12 rounded-xl border-gray-200 bg-white/50 focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="register-firstname-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-gray-700 font-medium">
                  {t('lastName')}
                </Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Dupont"
                  className="h-12 rounded-xl border-gray-200 bg-white/50 focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                {t('email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                  className="pl-12 h-12 rounded-xl border-gray-200 bg-white/50 focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_birth" className="text-gray-700 font-medium">
                {t('dateOfBirth')}
              </Label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="date_of_birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="pl-12 h-12 rounded-xl border-gray-200 bg-white/50 focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="register-dob-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">
                {t('password')}
              </Label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
                required
                testId="register-password-input"
              />
              <p className={`text-xs mt-1 ${formData.password && !isStrongPassword(formData.password) ? 'text-orange-600' : 'text-gray-500'}`} data-testid="register-password-hint">
                {language === 'fr' ? '8 caractères minimum, avec au moins une lettre et un chiffre' : 'At least 8 characters, with a letter and a number'}
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all text-lg font-semibold mt-6"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {t('createAccount')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              {t('hasAccount')}{' '}
              <Link 
                to="/login" 
                className="text-primary font-semibold hover:underline"
                data-testid="login-link"
              >
                {t('loginHere')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
